import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, X, Send, Bot, Coins } from 'lucide-react';
import toast from 'react-hot-toast';
import AiPackageDiscount from './ai-package-discount';
import { AiPackage, AiStatus, askAssistant, getAiStatus, purchaseAiPackage } from '../../api/ai/ai.api';
import { Link } from 'react-router-dom';

/**
 * Render text trả lời của AI, biến link markdown [tiêu đề](đường-dẫn) thành link BẤM ĐƯỢC.
 * Link nội bộ (/share/post/...) → <Link> điều hướng trong app; link http → mở tab mới.
 * (Chỉ parse cú pháp markdown do BE nhồi vào — không tự linkify URL trần để tránh biến tiêu đề-là-URL thành link sai.)
 */
const MD_LINK = /\[([^\]]+)\]\((\/[^)\s]+|https?:\/\/[^)\s]+)\)/g;
const renderRich = (text: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  MD_LINK.lastIndex = 0;
  while ((m = MD_LINK.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const label = m[1];
    const url = m[2];
    const cls = 'font-medium text-sky-600 underline underline-offset-2 hover:text-sky-700 dark:text-sky-400';
    if (url.startsWith('/')) {
      out.push(<Link key={key++} to={url} className={cls}>{label}</Link>);
    } else {
      out.push(<a key={key++} href={url} target="_blank" rel="noreferrer" className={cls}>{label}</a>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

/**
 * Trợ lý AI Cab — nút nổi góc phải dưới + bảng chat.
 *
 * Đã nối BE thật (/v1/post-service/ai-chat/ask — 5 lớp an toàn + trừ token).
 * BE đang chạy MOCK cho tới khi có OpenAI key (câu hỏi "bài hot" trả dữ liệu thật);
 * có key thì BE tự chuyển GPT thật, FE không cần đổi gì.
 */

type ChatRole = 'assistant' | 'user';
interface ChatMessage {
  id: number;
  role: ChatRole;
  text: string;
}

const SUGGESTIONS = [
  'Bài nào đang hot hôm nay?',
  'Xu hướng donate tuần này',
  'Bảng xếp hạng bang hội',
];

const INTRO: ChatMessage = {
  id: 0,
  role: 'assistant',
  text: 'Xin chào 👋 Mình là Trợ lý Cab. Hỏi mình về bài viết đang hot, xu hướng donate, hay bảng xếp hạng bang hội nhé!',
};

const MAX_INPUT_CHARS = 500; // đồng bộ với BE (AiChat:MaxInputChars)

/** Số token đầy đủ theo locale VN (14.987). */
const fmtTokens = (n: number) => n.toLocaleString('vi-VN');

/** Số token dạng gọn cho gói (300K, 1,8M). */
const fmtTokensCompact = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toLocaleString('vi-VN')}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`;

/** Thanh mức dùng token thật — xài bao nhiêu % chạy bấy nhiêu, đầy 100% (đỏ) = hết. */
const UsageBar = ({ label, used, total }: { label: string; used: number; total: number }) => {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-slate-500 dark:text-slate-400">{label}</span>
        <span className="font-medium text-slate-600 dark:text-slate-300">
          {fmtTokens(used)}/{fmtTokens(total)} · {pct}%
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? 'bg-rose-500' : 'bg-sky-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const AiChatWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([INTRO]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [showPackages, setShowPackages] = useState(false);
  const [buying, setBuying] = useState<string | null>(null);
  // Bước xác nhận trước khi mua (chống bấm nhầm mua luôn).
  const [confirmPkg, setConfirmPkg] = useState<AiPackage | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const idRef = useRef(1);

  const refreshStatus = async () => {
    try {
      setStatus(await getAiStatus());
    } catch {
      /* im lặng — không chặn chat nếu lấy số dư lỗi */
    }
  };

  // Lấy số dư token khi mở panel
  useEffect(() => {
    if (open) refreshStatus();
  }, [open]);

  const handleBuy = async (code: string) => {
    if (buying) return;
    setBuying(code);
    try {
      const res = await purchaseAiPackage(code);
      if (res.isSuccess) {
        toast.success('Mua token thành công');
        setShowPackages(false);
        setConfirmPkg(null);
        await refreshStatus();
      } else {
        toast.error(res.message || 'Mua token thất bại');
      }
    } catch {
      toast.error('Mua token thất bại');
    } finally {
      setBuying(null);
    }
  };

  // Cuộn xuống cuối khi có tin nhắn mới / đang gõ
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, typing]);

  // Mở panel -> focus ô nhập; Esc để đóng
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 250);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSend = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || typing) return;
    if (content.length > MAX_INPUT_CHARS) {
      setMessages((m) => [
        ...m,
        { id: idRef.current++, role: 'assistant', text: `Câu hỏi tối đa ${MAX_INPUT_CHARS} ký tự — bạn rút gọn giúp mình nhé.` },
      ]);
      return;
    }
    const userMsg: ChatMessage = { id: idRef.current++, role: 'user', text: content };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    // Chưa đăng nhập → không gọi API (sẽ 401), báo rõ ràng thay vì "đang gặp sự cố".
    if (!localStorage.getItem('accessToken')) {
      setMessages((m) => [
        ...m,
        { id: idRef.current++, role: 'assistant', text: 'Bạn vui lòng đăng nhập để sử dụng Trợ lý Cab nhé.' },
      ]);
      return;
    }
    setTyping(true);
    try {
      const res = await askAssistant(content);
      setMessages((m) => [...m, { id: idRef.current++, role: 'assistant', text: res.reply }]);
      // Cập nhật chip token theo số BE trả về (không cần gọi lại /status)
      if (!res.blocked) {
        setStatus((prev) =>
          prev ? { ...prev, totalAvailable: res.totalAvailable, canAsk: res.totalAvailable > 0 } : prev
        );
      }
      if (res.outOfTokens) setShowPackages(true);
    } catch (e: any) {
      // 401 (token hết hạn giữa chừng) → cũng nhắc đăng nhập; còn lại mới là "đang gặp sự cố".
      const unauth = e?.response?.status === 401;
      setMessages((m) => [
        ...m,
        {
          id: idRef.current++,
          role: 'assistant',
          text: unauth
            ? 'Phiên đăng nhập đã hết. Bạn vui lòng đăng nhập lại để tiếp tục nhé.'
            : 'Trợ lý Cab đang gặp sự cố, bạn thử lại sau chút nhé.',
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showSuggestions = messages.filter((m) => m.role === 'user').length === 0;

  return (
    <>
      {/* Nút nổi */}
      <button
        type="button"
        aria-label="Mở trợ lý Cab"
        onClick={() => setOpen((v) => !v)}
        className="group fixed bottom-5 right-5 z-[120] flex h-14 w-14 items-center justify-center
          rounded-full bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-lg
          shadow-sky-600/30 outline-none transition-transform duration-200
          hover:scale-105 active:scale-95 focus-visible:ring-4 focus-visible:ring-sky-500/40"
      >
        {/* vòng sáng nhịp nhẹ */}
        <span className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-sky-400/40 ai-chat-glow" />
        <span className="transition-transform duration-300 group-hover:rotate-12">
          {open ? <X size={24} /> : <Sparkles size={24} />}
        </span>
        {!open && (
          <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-white dark:ring-dark2" />
        )}
      </button>

      {/* Bảng chat */}
      {open && (
        <div
          role="dialog"
          aria-label="Trợ lý Cab"
          className="ai-chat-panel fixed bottom-24 right-5 z-[120] flex w-[380px] max-w-[calc(100vw-2.5rem)]
            flex-col overflow-hidden rounded-2xl border border-[#DEE0E6] bg-white shadow-2xl
            shadow-black/10 dark:border-slate-700 dark:bg-dark2
            h-[560px] max-h-[calc(100vh-8rem)]"
        >
          {/* Header */}
          <div className="relative flex items-center gap-3 bg-gradient-to-r from-sky-600 to-sky-500 px-4 py-3 text-white">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 backdrop-blur">
              <Bot size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-tight">Trợ lý Cab</p>
              <p className="flex items-center gap-1.5 text-xs text-white/80">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                Sẵn sàng hỗ trợ
              </p>
            </div>
            {status && (
              <button
                type="button"
                onClick={() => {
                  setShowPackages((v) => !v);
                  refreshStatus(); // cập nhật số liệu thanh mức dùng
                }}
                title="Số lượt Trợ lý còn lại — bấm xem mức dùng / mua thêm"
                className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs
                  font-semibold text-white backdrop-blur transition-colors hover:bg-white/25"
              >
                <Coins size={14} />
                {fmtTokens(status.totalAvailable)} token
              </button>
            )}
            <button
              type="button"
              aria-label="Đóng"
              onClick={() => setOpen(false)}
              className="rounded-full p-1.5 text-white/90 transition-colors hover:bg-white/15"
            >
              <X size={18} />
            </button>
          </div>

          {/* Danh sách tin nhắn */}
          <div
            ref={listRef}
            className="flex-1 space-y-3 overflow-y-auto bg-[#F4F5F8] px-4 py-4 dark:bg-slate-900/40"
          >
            {messages.map((m) =>
              m.role === 'assistant' ? (
                <div key={m.id} className="flex items-end gap-2">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white">
                    <Bot size={15} />
                  </div>
                  <div className="max-w-[78%] whitespace-pre-line rounded-2xl rounded-bl-md bg-white px-3.5 py-2.5 text-sm leading-relaxed text-slate-800 shadow-sm dark:bg-slate-800 dark:text-slate-100">
                    {renderRich(m.text)}
                  </div>
                </div>
              ) : (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[78%] rounded-2xl rounded-br-md bg-sky-600 px-3.5 py-2.5 text-sm leading-relaxed text-white shadow-sm">
                    {m.text}
                  </div>
                </div>
              )
            )}

            {/* Đang gõ */}
            {typing && (
              <div className="flex items-end gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white">
                  <Bot size={15} />
                </div>
                <div className="flex gap-1 rounded-2xl rounded-bl-md bg-white px-4 py-3 shadow-sm dark:bg-slate-800">
                  <span className="ai-chat-dot h-2 w-2 rounded-full bg-slate-400" />
                  <span className="ai-chat-dot h-2 w-2 rounded-full bg-slate-400" style={{ animationDelay: '0.15s' }} />
                  <span className="ai-chat-dot h-2 w-2 rounded-full bg-slate-400" style={{ animationDelay: '0.3s' }} />
                </div>
              </div>
            )}

            {/* Gợi ý câu hỏi */}
            {showSuggestions && !typing && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSend(s)}
                    className="rounded-full border border-sky-200 bg-white px-3 py-1.5 text-xs font-medium
                      text-sky-700 transition-colors hover:bg-sky-50
                      dark:border-slate-600 dark:bg-slate-800 dark:text-sky-300 dark:hover:bg-slate-700"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Bảng mua thêm token (mở từ chip token, hoặc tự hiện khi hết) */}
          {(showPackages || (status && status.totalAvailable <= 0)) && status && (
            <div className="border-t border-[#E5E7EB] bg-white px-4 py-3 dark:border-slate-700 dark:bg-dark2">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800 dark:text-white">
                  {status.totalAvailable <= 0 ? 'Hết token — mua thêm để tiếp tục' : 'Mua thêm lượt Trợ lý'}
                </p>
                {showPackages && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPackages(false);
                      setConfirmPkg(null);
                    }}
                    className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    Đóng
                  </button>
                )}
              </div>

              {/* Mức dùng token — thanh % chạy theo lượng đã dùng (input+output của AI đều được đếm) */}
              <div className="mb-3 space-y-2">
                <UsageBar
                  label="Miễn phí hôm nay"
                  used={status.freePerDay - status.freeRemaining}
                  total={status.freePerDay}
                />
                {status.monthlyQuota > 0 && (
                  <UsageBar
                    label="Gói CAB+ tháng này"
                    used={status.monthlyQuota - status.monthlyRemaining}
                    total={status.monthlyQuota}
                  />
                )}
                {status.tokenBalance > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500 dark:text-slate-400">Token đã mua (không hết hạn)</span>
                    <span className="font-semibold text-slate-700 dark:text-white">{fmtTokens(status.tokenBalance)} token</span>
                  </div>
                )}
              </div>

              {confirmPkg ? (
                /* Bước xác nhận — tránh bấm nhầm mua luôn */
                <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-center dark:border-slate-600 dark:bg-slate-800">
                  <p className="text-sm text-slate-700 dark:text-slate-100">
                    Mua{' '}
                    <b className="text-slate-900 dark:text-white">
                      {fmtTokensCompact(confirmPkg.tokens)} token
                    </b>{' '}
                    với giá{' '}
                    <b className="text-sky-600 dark:text-sky-300">
                      {confirmPkg.priceCa.toLocaleString('vi-VN')} Ca
                    </b>
                    {(confirmPkg.clanDiscountPercent ?? 0) > 0 && (
                      <>
                        {' '}
                        <s className="text-slate-400">{(confirmPkg.originalPriceCa ?? confirmPkg.priceCa).toLocaleString('vi-VN')} Ca</s>
                      </>
                    )}
                    ?
                  </p>
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                    Ca sẽ được trừ trong ví của bạn.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      disabled={!!buying}
                      onClick={() => setConfirmPkg(null)}
                      className="flex-1 rounded-lg border border-[#DEE0E6] py-2 text-sm font-medium text-slate-600
                        hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      Huỷ
                    </button>
                    <button
                      type="button"
                      disabled={!!buying}
                      onClick={() => handleBuy(confirmPkg.code)}
                      className="flex-1 rounded-lg bg-sky-600 py-2 text-sm font-semibold text-white
                        hover:bg-sky-700 disabled:opacity-60"
                    >
                      {buying ? 'Đang xử lý…' : 'Đồng ý mua'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    {status.packages.map((p) => (
                      <button
                        key={p.code}
                        type="button"
                        onClick={() => setConfirmPkg(p)}
                        className="flex flex-col items-center gap-0.5 rounded-xl border border-[#DEE0E6] bg-white px-2 py-2.5
                          text-center transition-colors hover:border-sky-500 hover:bg-sky-50
                          dark:border-slate-600 dark:bg-slate-800 dark:hover:border-sky-500 dark:hover:bg-slate-700"
                      >
                        <span className="text-sm font-bold text-slate-800 dark:text-white">{fmtTokensCompact(p.tokens)}</span>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">token</span>
                        <span className="mt-0.5 text-xs font-semibold text-sky-600 dark:text-sky-300">
                          {p.priceCa.toLocaleString('vi-VN')} Ca
                        </span>
                        <AiPackageDiscount pkg={p} />
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-center text-[11px] text-slate-400 dark:text-slate-500">
                    Chọn gói → xác nhận rồi mới trừ Ca
                  </p>
                </>
              )}
            </div>
          )}

          {/* Ô nhập */}
          <div className="border-t border-[#E5E7EB] bg-white p-3 dark:border-slate-700 dark:bg-dark2">
            <div className="flex items-end gap-2 rounded-2xl border border-[#C8CCD8] bg-white px-3 py-2 focus-within:border-sky-500 dark:border-slate-600 dark:bg-slate-800">
              <textarea
                ref={inputRef}
                rows={1}
                maxLength={MAX_INPUT_CHARS}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onInputKeyDown}
                placeholder="Nhập câu hỏi cho trợ lý…"
                className="max-h-24 flex-1 resize-none bg-transparent text-sm text-slate-800 outline-none
                  placeholder:text-slate-400 dark:text-white dark:placeholder:text-slate-500"
              />
              <button
                type="button"
                aria-label="Gửi"
                onClick={() => handleSend()}
                disabled={!input.trim() || typing}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white
                  transition-all hover:bg-sky-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send size={17} />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[11px] text-slate-400 dark:text-slate-500">
              Tùy độ phức tạp, mỗi câu hỏi có thể tính token khác nhau. Trợ lý Cab có thể chưa chính xác tuyệt đối.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AiChatWidget;
